/**
 * The currencies an employer may pick as an employee's pay currency —
 * `payroll.listPayCurrencies`: the entity base currency, a fixed set of common
 * payroll currencies, and every active tenant currency, restricted
 * server-side to 2-decimal ISO codes (v1 scope, so `MoneyInput`'s default
 * `decimals` and `lib/format.ts`'s fixed 100 minor-units-per-major stay
 * correct).
 *
 * Split in two so the employee form dialog stays renderable without an
 * `<AuthProvider>` (its tests render it bare):
 *  - {@link usePayCurrencyOptions} does the fetch; it is called from
 *    `use_employees.ts`'s hooks and surfaced as their `pay_currencies` field.
 *  - {@link withPayCurrencyFallbacks} is the pure merge the dialog applies,
 *    so the picker is always usable while loading / after a failed fetch,
 *    and a stored code the list no longer offers stays selectable. The
 *    server still validates whatever is submitted.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';

export interface PayCurrencyOption {
  code: string;
  name: string;
  decimals: number;
  is_base: boolean;
}

function fallbackOption(code: string, base: string): PayCurrencyOption {
  return { code, name: code, decimals: 2, is_base: code === base };
}

/**
 * Picker options: the fetched list (or `undefined` while loading / on error)
 * plus the base currency and the draft's current code if either is missing.
 * Pure — exported for tests.
 */
export function withPayCurrencyFallbacks(
  fetched: readonly PayCurrencyOption[] | undefined,
  base: string,
  current: string,
): PayCurrencyOption[] {
  const options = [...(fetched ?? [])];
  const known = new Set(options.map((c) => c.code));
  for (const code of [base, current]) {
    if (code.length > 0 && !known.has(code)) {
      options.push(fallbackOption(code, base));
      known.add(code);
    }
  }
  return options.sort((a, b) => a.code.localeCompare(b.code));
}

/** Fetches `payroll.listPayCurrencies`; `undefined` until it resolves, and after a failure. Never throws. */
export function usePayCurrencyOptions():
  | readonly PayCurrencyOption[]
  | undefined {
  const entityContext = useOAuthEntityContext();
  const [fetched, setFetched] = React.useState<
    readonly PayCurrencyOption[] | undefined
  >(undefined);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const input = {
      firm_id: entityContext.firm_id,
      entity_id: entityContext.entity_id,
    };
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const result =
          await getTrpcClient().payroll.listPayCurrencies.query(input);
        if (!cancelled) setFetched(result.currencies);
      } catch (err: unknown) {
        // The picker falls back to base + current code; the server validates the code on save.
        console.warn('[wagepro-web] payroll.listPayCurrencies failed:', err);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext]);

  return fetched;
}
